import { IControlParameters } from '@Common/Controls/BaseControl';
import { Splitter } from '@Common/Controls/Splitter';
import { safeDestroys } from '@Common/Core/Destroyable';
import { isJQuery } from '@Common/Helpers/CommonUtils';

export interface ILayoutParameters extends IControlParameters {
    titleHeight?: number;

    splitterMin?: number;
    splitterMax?: number;

    layoutClass?: string;
    titleClass?: string;
    contentClass?: string;

    columns?: number;
    rows?: number;

    reverseCollapse?: boolean;

    cellClass?: string;
}

export interface ILayoutAdjustParams {
    proportionally?: boolean;
}

export class Layout {
    private readonly _param: ILayoutParameters;
    private firstAdjust: boolean;
    private current: any;
    private cellControls: { item; cell }[];
    private splitters: any[];
    private hasSplitter: boolean;
    private sizes: any[];
    private table: JQuery<HTMLElement>;

    constructor(param: ILayoutParameters) {
        this._param = $.extend(
            {
                css: null,
                className: '',
                width: '100%',
                height: '100%',

                titleHeight: 30,

                splitterMin: 5,
                splitterMax: 9,

                layoutClass: '',
                titleClass: '',
                contentClass: '',
                columns: 2
            },
            param
        );

        const resize = $.noop;
        this.firstAdjust = true;
        this.current = null;
        this.cellControls = [];
        this.splitters = [];
        this.hasSplitter = false;
        this.table = $(`
<table class="layout" cellpadding="0" cellspacing="0">
    <thead></thead>
    <tbody></tbody>
</table>`)
            .width(this._param.width)
            .height(this._param.height)
            .addClass(this._param.layoutClass);
        // {index: int, width: int, height: int}
        this.sizes = [];
    }

    public setValue(items): void {
        this.current = items;
        this.splitters = [];
        this.hasSplitter = false;
        this.detachPreviousControls();
        this.cellControls = [];
        this.table.children('tbody').empty();

        let tr = $('<tr/>').appendTo(this.table);
        let checker = 0;

        _(this.current).forEach((item, index) => {
            let tdWidth;
            if (item.minWidth && item.width && item.width < item.minWidth) {
                tdWidth = item.minWidth;
            } else if (item.width) {
                tdWidth = item.width;
            } else {
                tdWidth = 'auto';
            }

            let tdHeight;
            if (item.minHeight && item.height && item.height < item.minHeight) {
                tdHeight = item.minHeight;
            } else if (item.height) {
                tdHeight = item.height;
            } else {
                tdHeight = 'auto';
            }

            const td = new Cell({
                disableSplit: item.disableSplit || false,
                width: tdWidth,
                height: tdHeight,
                colspan: item.colspan,
                rowspan: item.rowspan,
                onResize: item.onResize || null,
                isCollapsed: item.isCollapsed,
                onAutoWidth: item.onAutoWidth,
                css: item.css,
                contentClass: this._param.contentClass,
                titleClass: this._param.titleClass,
                table: this.table,
                layout: this
            });

            td.setValue(item);
            td.getControl().appendTo(tr);
            td.onCloseCell((cell, cellData) => {
                //remove splitters
                cell.next().hasClass('splitter') && cell.next().remove();
                cell.prev().hasClass('splitter') && cell.prev().remove();
                if (cell.parent().children('td').length == 1 && cell.parent().next().hasClass('splitter')) {
                    cell.parent().next().remove();
                }

                cell.remove();
                this.adjust();
            });

            td.onToggleCell((isCollapsed, cellData) => {
                cellData.isCollapsed = isCollapsed;

                this.setValue(this.current);
                this.addingSplitters();

                isCollapsed ? item.onCollapse && item.onCollapse() : item.onExpand && item.onExpand();
            });

            this.cellControls.push({ item: item, cell: td });

            checker += item.colspan || 1;

            // if need make new tr
            if (checker >= this._param.columns && items.length - 1 != index) {
                tr = $('<tr/>').appendTo(this.table);
                checker = 0;

                const rowSpanCount = _(this.table.children('tbody').children('tr').children('td'))
                    .filter(i => parseInt($(i).attr('rowspan')) > 1)
                    .size();

                checker += rowSpanCount;
            }

            // expand/collapse item
            item.collapse = () => {
                td.toggle(true);
                item.onCollapse && item.onCollapse();
                this.adjust();
            };

            item.expand = () => {
                td.toggle(false);
                item.onExpand && item.onExpand();
                this.adjust();
            };

            // get size from item
            item.getOuterSize = () => ({
                width: td.getControl().outerWidth(true),
                height: td.getControl().outerHeight(true)
            });

            item.getState = () => {
                if (item.isCollapsed) {
                    return {
                        height: item.height,
                        isCollapsed: true,
                        width: item.width
                    };
                } else {
                    return {
                        height: td.getControl().outerHeight(true),
                        isCollapsed: false,
                        width: td.getControl().outerWidth(true)
                    };
                }
            };

            item.setState = data => {
                if (data) {
                    item.isCollapsed = data.isCollapsed;
                    item.width = data.width || 'auto';
                    item.height = data.height || 'auto';
                    this.setValue(this.current);
                    this.addingSplitters();
                }
            };

            item.setContent = newContent => {
                item.control.content = newContent;
                td.setValue(item);
                this.adjust();
            };

            // set size for item
            item.setOuterSize = newSize => {
                if (newSize && (newSize.width || newSize.height)) {
                    item.width = newSize.width || 'auto';
                    item.height = newSize.height || 'auto';
                    this.setValue(this.current);
                    this.addingSplitters();
                }
            };

            item.destroy = () => {
                td.destroy();
            };
        });
    }

    public detachPreviousControls(): void {
        for (let {
            item: {
                control: { content }
            }
        } of this.cellControls) {
            if ('getControl' in content) {
                content = content.getControl();
            }

            if (isJQuery(content)) {
                content.detach();
            }
        }
    }

    public addingSplitters(): void {
        // if layout using in component
        // function addingSplitter can be call many time
        // but need use it only once for layout instance
        if (this.hasSplitter) {
            //hasSplitter = false;
            return;
        }

        try {
            this.createHorizontalSplitters();
            this.createVerticalSplitters();
            this.setCollapsedStyles();

            this.table.children('tbody').children('tr').children('td.splitter').draggable();

            this.hasSplitter = true;
        } finally {
            //
        }
        this.adjust();
    }

    private createHorizontalSplitters(): void {
        const trs = _(this.table.children('tbody').children('tr'))
            .map(_.unary($))
            .filter(tr => _.some(tr.children('td'), td => !$(td).hasClass('disable-split')))
            .value();

        trs.forEach((tr: JQuery, trIndex) => {
            if (trIndex < trs.length - 1) {
                const colspan = _(tr.children('td'))
                    .map(_.unary($))
                    .filter(i => parseInt(i.attr('rowspan') || '1') == 1 && !i.hasClass('splitter'))
                    .reduce((sum, i) => sum + (parseInt(i.attr('colspan')) || 1), 0);

                const splitter = new Splitter({
                    isVertical: false,
                    colspan: colspan,
                    width: tr.outerWidth(true),
                    containment: this.table,
                    splitterMin: this._param.splitterMin,
                    disable: tr.next().children(':first').hasClass('collapsed') || tr.children(':first').hasClass('collapsed')
                });

                this.splitters.push(splitter);
                splitter.onSplitEnd(offset => {
                    _(tr.children('td'))
                        .map(_.unary($))
                        .filter(td => !td.hasClass('splitter') && td.attr('rowspan') == '1')
                        .forEach(td => {
                            const data = td.data('item');
                            if (data.height != 'auto') {
                                data.height = data.height - offset;
                            } else {
                                data.height = td.outerHeight(true) - offset;
                            }
                        });

                    _(tr.next().next().children('td'))
                        .map(_.unary($))
                        .filter(td => !td.hasClass('splitter'))
                        .forEach(td => {
                            const data = td.data('item');
                            if (data.height != 'auto') {
                                data.height = data.height + offset;
                            } else {
                                data.height = td.outerHeight(true) + offset;
                            }
                        });

                    this.setValue(this.current);
                    this.addingSplitters();
                });

                $('<tr/>').append(splitter.getControl()).insertAfter(tr);

                _(this.table.children('tbody').children('tr').children('td'))
                    .map($)
                    .filter(i => parseInt(i.attr('rowspan') || '1') > 1)
                    .forEach(i => {
                        i.attr('rowspan', parseInt(i.attr('rowspan')) + 1);
                    });
            }
        });
    }

    private createVerticalSplitters(): void {
        const trs = this.table.children('tbody').children('tr');
        _(trs)
            .map(_.unary($))
            .forEach(tr => {
                _(tr.children('td'))
                    .map(_.unary($))
                    .filter(td => !td.hasClass('disable-split'))
                    .forEach((td, tdIndex) => {
                        if (tdIndex != 0) {
                            const rowspan = _(tr.children('td'))
                                .map(i => parseInt($(i).attr('rowspan') || '1'))
                                .max();
                            const splitter = new Splitter({
                                disable: td.prev().hasClass('collapsed') || /*td.next().hasClass('collapsed') || */ td.hasClass('collapsed'),
                                rowspan: rowspan,
                                height: td.outerHeight(true),
                                containment: this.table,
                                splitterMin: this._param.splitterMin
                            });

                            this.splitters.push(splitter);

                            splitter.getControl().insertBefore(td);
                            splitter.onSplitEnd(offset => {
                                const data = td.data('item');
                                const prevTd = td.prev().prev();
                                const prevTdItem = prevTd.data('item');

                                const oldSize = prevTdItem.width;

                                if (data.width != 'auto') {
                                    data.width = data.width + offset;
                                } else if (prevTdItem.width != 'auto') {
                                    prevTdItem.width = prevTdItem.width - offset;
                                } else if (data.width == 'auto' && prevTdItem.width == 'auto') {
                                    data.width = td.outerWidth(true) + offset;
                                }

                                if (td.attr('rowspan') == '2') {
                                    let index;

                                    _(prevTd.parent().children('td'))
                                        .map(_.unary($))
                                        .forEach((i, ind) => {
                                            if (i.data('item') == prevTdItem) {
                                                index = ind;
                                                return false;
                                            }
                                        });

                                    _(td.parent().next().children('td'))
                                        .map(_.unary($))
                                        .filter((i, ind) => ind == index && i.data('item').width == oldSize)
                                        .forEach(i => {
                                            i.data('item').width = data.width;
                                        });

                                    _(td.parent().prev().children('td'))
                                        .map(_.unary($))
                                        .filter((i, ind) => ind == index && i.data('item').width == oldSize)
                                        .forEach(i => {
                                            i.data('item').width = data.width;
                                        });
                                }

                                this.setValue(this.current);
                                this.addingSplitters();
                            });

                            _(this.table.children('tbody').children('tr').children('td'))
                                .map(_.unary($))
                                .filter(i => parseInt(i.attr('colspan') || '1') > 1)
                                .forEach(i => {
                                    i.attr('colspan', parseInt(i.attr('colspan') || '1') + 1);
                                });
                        }
                    });
            });
    }

    private setCollapsedStyles(): void {
        const trs = this.table.children('tbody').children('tr');
        const rowCount = trs.length;
        const columnCount =
            _(trs)
                .map(i => $(i).children('td').length)
                .max() || 1;
        _(this.table.children('tbody').children('tr').children('td'))
            .map(_.unary($))
            .filter(i => !i.hasClass('splitter'))
            .forEach(i => {
                const rowspan = parseInt(i.attr('rowspan')) || 1;
                const colspan = parseInt(i.attr('colspan')) || 1;

                if (rowCount == rowspan) {
                    i.addClass('vertical-collapsed');
                } else if (columnCount == colspan) {
                    i.addClass('horizontal-collapsed');
                } else if (
                    rowspan == 1 &&
                    colspan == 1 &&
                    _(i.parents('tr:first').children('td'))
                        .map(_.unary($))
                        .filter(n => !n.hasClass('splitter') && parseInt(n.attr('rowspan') || '1') == 1)
                        .size() == 1
                ) {
                    i.addClass('horizontal-collapsed');
                } else {
                    i.addClass('vertical-collapsed');
                }
            });
    }

    public adjust(width?, height?, adjustParam?: ILayoutAdjustParams): void {
        if (width && height) {
            this.table.css({ width: width, height: height });
        }

        const newSizes = [];

        if (this.table.elementInDOM()) {
            _(this.cellControls).forEach((cellControl, index) => {
                const td = cellControl.cell.getControl();
                td.height('auto');
            });

            _(this.cellControls).forEach((cellControl, index) => {
                adjustParam = $.extend(adjustParam, {
                    tableWidth: parseInt(width, 10),
                    tableHeight: parseInt(height, 10)
                });

                const size = cellControl.cell.adjust(adjustParam);
                newSizes.push({ index: index, width: size.width, height: size.height });

                if (
                    (this.sizes && this.sizes.length == 0) ||
                    _(this.sizes)
                        .filter(i => i.index == index)
                        .some(i => i.height != size.height || i.width != size.width)
                ) {
                    cellControl.cell.onResize &&
                        cellControl.cell.onResize({
                            width: size.width,
                            height: size.height
                        });
                }

                if (cellControl.item.control.content && cellControl.item.control.content.adjust) {
                    cellControl.item.control.content.adjust(size.width, size.height);
                }
            });
            _(this.splitters).forEach(splitter => {
                //debugger;
                splitter.isVertical() && splitter.getSplitterArea().height(splitter.getControl().height() || height);
                !splitter.isVertical() && splitter.getSplitterArea().width(splitter.getControl().width() || width);
            });

            this.sizes = newSizes;
            this.firstAdjust = false;
        }
    }

    public destroy(): void {
        this.table.remove();
        this.table = null;

        _(this.cellControls).forEach(({ item }) => {
            item.destroy && item.destroy();
        });

        this.cellControls = null;
        this.splitters = null;
    }

    public readonly(data): void {
        _(this.cellControls).forEach(({ item }) => {
            item.readonly && item.readonly(data);
        });
    }

    public getControl(): JQuery {
        return this.table;
    }

    public updateCellTitle(cellNum: number, text: string): void {
        this.cellControls[cellNum].cell.setTitle(text);
    }
}

/* Layout.Cell */
class Cell {
    _cellParam: any;
    _onCloseCell: IHandler;
    _onToggleCell: IHandler;
    _titleHeight: number;
    _currentData: any;
    _layoutCell: JQuery<HTMLElement>;
    _cell: any;
    _cellContent: any;
    _title: Title;

    constructor(cellParam) {
        this._cellParam = $.extend(
            {
                disableSplit: false,
                height: 'auto',
                width: 'auto',
                colspan: 1,
                rowspan: 1,
                showTitile: true,
                isCollapsed: false,
                onResize: null,
                onAutoWidth: null,
                css: {},
                cellClass: '',
                table: null,
                contentClass: '',
                titleClass: '',
                layout: null
            },
            cellParam
        );

        this._onCloseCell = $.noop;
        this._onToggleCell = $.noop;

        this._titleHeight = 30;

        this._currentData;

        this._layoutCell = $('<td class="layout-td"/>')
            .addClass(this._cellParam.isCollapsed ? 'collapsed' : 'expanded')
            .addClass(this._cellParam.cellClass)
            .toggleClass('disable-split', this._cellParam.disableSplit)
            .attr('colspan', this._cellParam.colspan)
            .attr('rowspan', this._cellParam.rowspan);

        this._layoutCell.outerWidth(this._cellParam.width);
        this._layoutCell.outerHeight(this._cellParam.height);

        this._cell;
        this._cellContent;
    }

    public getControl() {
        return this._layoutCell;
    }

    public destroy(): void {
        this._onCloseCell = null;
        this._onToggleCell = null;
        safeDestroys(this._layoutCell, this._currentData?.control?.content);
    }

    public setValue(data) {
        this._currentData = data;

        !this._currentData.height && (this._currentData.height = 'auto');
        !this._currentData.width && (this._currentData.width = 'auto');

        this._layoutCell.children('*').detach();
        //layoutCell.empty();
        this._layoutCell.data('item', data);

        this._cell = $('<div class="layout-cell"/>').appendTo(this._layoutCell);

        if (!data.hideHeaders) {
            this._title = new Title(data, { titleClass: this._cellParam.titleClass });
            this._title.getControl().appendTo(this._cell);
            this._title.onToggleCollapse(
                function () {
                    this._onToggleCell(!this._layoutCell.hasClass('collapsed'), this._currentData);
                }.bind(this)
            );

            this._title.onClose(
                function () {
                    this._onCloseCell(this._layoutCell, this._currentData);
                }.bind(this)
            );
            this._title.onOptimalWidth(
                function () {
                    const extraPadding = this._cellContent.outerWidth() - this._cellContent.width();
                    this._cellParam.onAutoWidth && this._cellParam.onAutoWidth(extraPadding);
                }.bind(this)
            );
        }

        this._cellContent = $('<div class="layout-cell-content"/>')
            .addClass(data.contentClass)
            .addClass(this._cellParam.contentClass)
            .css(this._cellParam.css)
            .appendTo(this._cell);

        if (data.control.content && data.control.content.getControl) {
            this._cellContent.append(data.control.content.getControl());
        } else {
            this._cellContent.append(data.control.content);
        }

        this.toggle(this._cellParam.isCollapsed);
    }

    public toggle(data) {
        this._cellParam.isCollapsed = data;
        this._cellParam.isCollapsed
            ? this._layoutCell.removeClass('expanded').addClass('collapsed')
            : this._layoutCell.removeClass('collapsed').addClass('expanded');
    }

    public adjust(adjustParam) {
        let w, h, p;

        const tableWidth = (adjustParam && parseInt(adjustParam.tableWidth, 10)) || this._cellParam.table.outerWidth(true);
        let tableHeight = (adjustParam && parseInt(adjustParam.tableHeight, 10)) || this._cellParam.table.outerHeight(true);

        tableHeight -= _(this._cellParam.layout._splitters)
            .filter(function (splitter) {
                return !splitter.isVertical();
            })
            .sumBy(function (splitter) {
                return splitter.getControl().height();
            });

        // width
        if (adjustParam && adjustParam.proportionally && this._currentData.proportionallyW) {
            p = parseFloat(this._currentData.proportionallyW.toFixed(2));
            w = Math.round(tableWidth * p);
            this._layoutCell.width(w);
        } else if (this._cellParam.width.indexOf && this._cellParam.width.indexOf('%') > 0) {
            const pw = (tableWidth * parseInt(this._cellParam.width.replace('%'))) / 100;
            this._layoutCell.outerWidth(pw);
        } else {
            w = this._cellParam.width;
            this._layoutCell.css('width', this._cellParam.width);
        }

        // heigth
        if (adjustParam && adjustParam.proportionally && this._currentData.proportionallyH) {
            p = parseFloat(this._currentData.proportionallyH.toFixed(2));
            h = Math.round(tableHeight * p);
            this._layoutCell.height(h);
        } else if (this._cellParam.height.indexOf && this._cellParam.height.indexOf('%') > 0) {
            const ph = (tableHeight * parseInt(this._cellParam.height.replace('%'))) / 100;
            this._layoutCell.outerHeight(ph);
        } else {
            h = this._cellParam.height;
            this._layoutCell.css('height', this._cellParam.height);
        }

        h = this._layoutCell.height() || h;
        w = this._layoutCell.width() || w;

        !this._currentData.hideHeaders && (h = h - this._titleHeight);

        this._cell.width(w);

        this._cellContent.css('height', h + 'px');
        this._cellContent.css('width', w + 'px');

        if (!this._cellParam.isCollapsed) {
            this._currentData.height != 'auto' && (this._currentData.height = parseInt(this._layoutCell.css('height').replace('px', '')));

            this._currentData.width != 'auto' && (this._currentData.width = parseInt(this._layoutCell.css('width').replace('px', '')));
        }

        this._currentData.proportionallyH = this._cellContent.height() / tableHeight;
        this._currentData.proportionallyW = this._cellContent.width() / tableWidth;

        return { height: this._cellContent.height(), width: this._cellContent.width() };
    }

    public setTitle(text) {
        this._currentData.control.title = text;
        this._title.update(text);
    }

    public onResize() {
        this._cellParam.onResize &&
            this._cellParam.onResize({
                height: this._cellContent.height(),
                width: this._cellContent.width()
            });
    }

    public onCloseCell(fn) {
        $.isFunction(fn) && (this._onCloseCell = fn);
    }

    public onToggleCell(fn) {
        $.isFunction(fn) && (this._onToggleCell = fn);
    }
}

/* Cell.prototype.adjustCell */
class Title {
    _onToggleCollapse: IHandler;
    _onClose: IHandler;
    _onOptimalWidth: IHandler;
    _cellTitle: JQuery<HTMLElement>;
    _button: JQuery<HTMLElement>;
    _title: JQuery<HTMLElement>;

    constructor(data, titleParam) {
        this._onToggleCollapse = $.noop;
        this._onClose = $.noop;
        this._onOptimalWidth = $.noop;

        this._cellTitle = $('<div class="layout-cell-title"/>').addClass((titleParam && titleParam.titleClass) || '');

        this._button = $('<div class="layout-cell-title-button"></div>').appendTo(this._cellTitle);

        !data.hideOptimalWidth &&
            $('<span class="icon optimal"/>')
                .appendTo(this._button)
                .click(
                    function () {
                        this._onOptimalWidth();
                    }.bind(this)
                );

        !data.hideCollapse &&
            $('<span class="icon pin"/>')
                .appendTo(this._button)
                .click(
                    function () {
                        this._onToggleCollapse();
                    }.bind(this)
                );

        !data.hideClose &&
            $('<span class="icon close"/>')
                .appendTo(this._button)
                .click(
                    function () {
                        this._onClose();
                    }.bind(this)
                );

        this._title = $('<div class="layout-cell-title-content text-no-wrap"/>').html(data.control.title).appendTo(this._cellTitle);
    }

    update(text) {
        this._title.html(text);
    }

    getControl() {
        return this._cellTitle;
    }

    onToggleCollapse(fn) {
        $.isFunction(fn) && (this._onToggleCollapse = fn);
    }

    onClose(fn) {
        $.isFunction(fn) && (this._onClose = fn);
    }

    onOptimalWidth(fn) {
        $.isFunction(fn) && (this._onOptimalWidth = fn);
    }
}
